// src/components/TopNav/PermissionsSettings.jsx
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Cog6ToothIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import Modal from '../Shared/Modal'
import Input from '../Shared/Input'
import Button from '../Shared/Button'
import usePermissions from '../../hooks/usePermissions'
import clsx from 'clsx'

export default function PermissionsSettings() {
  const { id: recipeId } = useParams()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('viewer')

  const {
    team: collaborators = [],
    loading,
    addPermission: addCollaborator,
    updatePermission: updateCollaboratorRole,
  } = usePermissions(recipeId)

  const handleOpen = () => setOpen(true)

  const handleInvite = async () => {
    if (!email) return
    try {
      await addCollaborator(email, role)
      toast.success(`Added ${email} (${role})`)
      setEmail('')
    } catch {
      toast.error('Failed to add collaborator')
    }
  }

  const handleRoleChange = async (email, newRole) => {
    try {
      await updateCollaboratorRole(email, newRole)
      toast.success(`Updated ${email} to ${newRole}`)
    } catch {
      toast.error('Failed to update role')
    }
  }

  // Role badge color classes
  const roleBadgeClass = {
    viewer: 'bg-light-gray text-gray-700',
    editor: 'bg-blue-100 text-blue-800',
    owner: 'bg-yellow-100 text-yellow-800',
  }

  const avatarBgClass = {
    viewer: 'bg-gray-400',
    editor: 'bg-blue-500',
    owner: 'bg-rich-gold',
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className="hidden md:block ml-4 p-2 rounded hover:bg-soft-yellow/40 focus:outline-none"
        aria-label="Recipe permissions"
        title="Manage access for this recipe"
      >
        <Cog6ToothIcon className="h-6 w-6" />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Permissions">
        <ul className="divide-y rounded border mb-6">
          {collaborators.map(({ email, role }) => (
            <li
              key={email}
              className="flex items-center justify-between px-3 py-2 text-sm transition-all animate-fade-in"
            >
              <span className="truncate flex items-center gap-2">
                <span
                  className={clsx(
                    'inline-block text-white rounded-full h-6 w-6 flex items-center justify-center text-xs font-bold',
                    avatarBgClass[role] || 'bg-gray-400'
                  )}
                  title={`Role: ${role}`}
                >
                  {email.charAt(0).toUpperCase()}
                </span>
                <span>{email}</span>
              </span>

              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    'text-xs px-2 py-0.5 rounded-full font-medium',
                    roleBadgeClass[role] || 'bg-gray-200 text-gray-600'
                  )}
                >
                  {role}
                </span>

                <select
                  value={role}
                  onChange={(e) => handleRoleChange(email, e.target.value)}
                  className="bg-white border rounded text-xs px-1 py-0.5"
                  title={`Change role for ${email}`}
                >
                  <option value="viewer" title="Viewer – can view only">Viewer</option>
                  <option value="editor" title="Editor – can edit">Editor</option>
                  <option value="owner" title="Owner – full access">Owner</option>
                </select>
              </div>
            </li>
          ))}
          {!collaborators.length && (
            <li className="px-3 py-4 text-sm text-slate-500">
              No collaborators yet.
            </li>
          )}
        </ul>

        <div className="flex gap-2">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="flex-1"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="border rounded text-xs"
            title="Select role to assign"
          >
            <option value="viewer" title="Viewer – can only view">Viewer</option>
            <option value="editor" title="Editor – can modify recipes">Editor</option>
          </select>

          <Button
            onClick={handleInvite}
            icon={PlusIcon}
            tooltip="Invite collaborator"
            disabled={loading}
          />
        </div>

        <p className="text-xs text-slate-600 mt-4 flex items-center gap-1">
          <XMarkIcon className="h-4 w-4" />
          Recipes auto-lock when another user is editing.
        </p>
      </Modal>
    </>
  )
}
